import { ListMusic, Plus } from 'lucide-react'
import { useCallback, useRef } from 'react'

import { Button } from '@/components/ui/button'
import type { MusicPlaylist } from '@/lib/db'

type PlaylistChipsProps = {
  playlists: MusicPlaylist[]
  activePlaylistId: string | null
  onSelect: (playlistId: string | null) => void
  onCreate: () => void
  onEdit: (playlist: MusicPlaylist) => void
}

/**
 * プレイリスト横スクロールチップバー
 *
 * - 「すべて」チップ（常に先頭）
 * - 各プレイリスト名チップ
 * - 「+」作成ボタン（常に末尾）
 * - ロングプレス(700ms)で編集Sheet起動
 */
export const PlaylistChips = ({ playlists, activePlaylistId, onSelect, onCreate, onEdit }: PlaylistChipsProps) => {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressFired = useRef(false)

  const handlePointerDown = useCallback(
    (playlist: MusicPlaylist) => {
      longPressFired.current = false
      longPressTimer.current = setTimeout(() => {
        longPressFired.current = true
        onEdit(playlist)
      }, 700)
    },
    [onEdit],
  )

  const handlePointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const handleClick = useCallback(
    (playlistId: string) => {
      if (longPressFired.current) return
      onSelect(playlistId)
    },
    [onSelect],
  )

  return (
    <div className="flex items-center gap-2 overflow-x-auto scrollbar-none pb-1 -mx-1 px-1">
      {/* すべてチップ */}
      <Button
        variant={activePlaylistId === null ? 'default' : 'outline'}
        size="sm"
        className="shrink-0 rounded-full text-xs h-8 px-3"
        onClick={() => onSelect(null)}
      >
        <ListMusic size={14} className="mr-1" />
        すべて
      </Button>

      {/* プレイリストチップ */}
      {playlists.map((pl) => (
        <Button
          key={pl.id}
          variant={activePlaylistId === pl.id ? 'default' : 'outline'}
          size="sm"
          className="shrink-0 rounded-full text-xs h-8 px-3 select-none"
          onPointerDown={() => handlePointerDown(pl)}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onClick={() => handleClick(pl.id)}
        >
          {pl.name}
        </Button>
      ))}

      {/* 新規作成ボタン */}
      <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8 rounded-full" onClick={onCreate}>
        <Plus size={16} />
      </Button>
    </div>
  )
}
